import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Bell, CheckCheck, Clock, Play, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, NavLink, Route, Routes } from "react-router-dom";
import Markdown from "@/components/chat/Markdown";
import { Badge, Button, Card, ErrorText } from "@/components/ui";
import { api } from "@/lib/api";
import { relTime, useTasks, type Notification, type Task } from "@/lib/tasks";
import TaskDetail from "./TaskDetail";
import TaskForm from "./TaskForm";

export function StatusBadge({ status }: { status: string | null }) {
  if (!status) return null;
  const tone = status === "done" ? "green" : status === "error" ? "red" : status === "running" || status === "starting" ? "indigo" : "zinc";
  return <Badge tone={tone}>{status}</Badge>;
}

export default function TasksPage() {
  return (
    <Routes>
      <Route index element={<TasksHome tab="tasks" />} />
      <Route path="inbox" element={<TasksHome tab="inbox" />} />
      <Route path=":id" element={<TaskDetail />} />
    </Routes>
  );
}

function TasksHome({ tab }: { tab: "tasks" | "inbox" }) {
  const unread = useQuery({ queryKey: ["notifications", "unread"], queryFn: () => api.get<{ count: number }>("/api/notifications/unread-count").then((r) => r.count) });
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto max-w-4xl px-4 py-6 md:px-8">
        <h1 className="mb-4 pl-10 text-2xl font-semibold md:pl-0">Tasks</h1>
        <nav className="mb-6 flex gap-1 border-b border-zinc-200 dark:border-zinc-800">
          {[
            { to: "/tasks", label: "Tasks", end: true },
            { to: "/tasks/inbox", label: `Inbox${unread.data ? ` (${unread.data})` : ""}`, end: true },
          ].map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              className={({ isActive }) =>
                clsx("-mb-px border-b-2 px-3 py-2 text-sm", isActive ? "border-indigo-600 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100")
              }
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
        {tab === "inbox" ? <Inbox /> : <TaskList />}
      </div>
    </div>
  );
}

function TaskList() {
  const qc = useQueryClient();
  const tasks = useTasks();
  const [editing, setEditing] = useState<Task | "new" | null>(null);
  const run = useMutation({ mutationFn: (id: string) => api.post(`/api/tasks/${id}/run`), onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }) });
  return (
    <Card
      title="Your tasks"
      actions={
        <Button size="sm" onClick={() => setEditing("new")}>
          <Plus size={16} /> New task
        </Button>
      }
    >
      <p className="mb-3 text-sm text-zinc-500">The assistant runs a task on a schedule or when a webhook is called, works on its own with the tools you allow, and notifies you only when something deserves your attention.</p>
      <ErrorText error={run.error} />
      {tasks.data?.length === 0 && <p className="text-sm text-zinc-500">No tasks yet.</p>}
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {tasks.data?.map((t) => (
          <li key={t.id} className="flex items-center gap-4 py-2.5">
            <Link to={`/tasks/${t.id}`} className="min-w-0 flex-1">
              <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                {t.name} {!t.enabled && <Badge tone="amber">paused</Badge>} <StatusBadge status={t.last_status} />
              </p>
              <p className="flex items-center gap-1 text-xs text-zinc-500">
                <Clock size={12} /> {t.trigger_text}
                {t.next_run_at && <> · next {relTime(t.next_run_at)}</>}
                {t.last_run_at && <> · last {relTime(t.last_run_at)}</>}
              </p>
            </Link>
            <Button size="sm" variant="secondary" onClick={() => run.mutate(t.id)} disabled={run.isPending}>
              <Play size={14} /> Run now
            </Button>
          </li>
        ))}
      </ul>
      {editing && <TaskForm task={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </Card>
  );
}

function Inbox() {
  const qc = useQueryClient();
  const items = useQuery({ queryKey: ["notifications", "list"], queryFn: () => api.get<Notification[]>("/api/notifications") });
  const tasks = useTasks().data ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["notifications"] });
  const read = useMutation({ mutationFn: (ids: string[] | null) => api.post("/api/notifications/read", { ids }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/api/notifications/${id}`), onSuccess: refresh });
  const [open, setOpen] = useState<string | null>(null);

  return (
    <Card
      title="Inbox"
      actions={
        <Button size="sm" variant="secondary" onClick={() => read.mutate(null)}>
          <CheckCheck size={14} /> Mark all read
        </Button>
      }
    >
      {items.data?.length === 0 && <p className="text-sm text-zinc-500">No notifications.</p>}
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {items.data?.map((n) => {
          const task = tasks.find((t) => t.id === n.task_id);
          const expanded = open === n.id;
          return (
            <li key={n.id} className="py-2.5">
              <div className="flex items-start gap-3">
                <Bell size={16} className={clsx("mt-0.5 shrink-0", n.read_at ? "text-zinc-300 dark:text-zinc-700" : n.priority === "high" ? "text-red-500" : "text-indigo-500")} />
                <button
                  className="min-w-0 flex-1 text-left"
                  onClick={() => {
                    setOpen(expanded ? null : n.id);
                    if (!n.read_at) read.mutate([n.id]);
                  }}
                >
                  <p className={clsx("text-sm", !n.read_at && "font-semibold")}>{n.title}</p>
                  <p className="text-xs text-zinc-500">
                    {task ? `${task.name} · ` : ""}
                    {relTime(n.created_at)}
                  </p>
                </button>
                <button className="rounded p-1 text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800" onClick={() => remove.mutate(n.id)} aria-label="Delete">
                  <Trash2 size={14} />
                </button>
              </div>
              {expanded && (
                <div className="mt-2 ml-7 rounded-lg bg-zinc-50 p-3 text-sm dark:bg-zinc-900">
                  <Markdown text={n.body || "(no details)"} />
                  {n.task_run_id && task && (
                    <Link to={`/tasks/${task.id}?run=${n.task_run_id}`} className="mt-2 inline-block text-xs text-indigo-600 hover:underline dark:text-indigo-400">
                      Open the run
                    </Link>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
